import { type ChangeHistory, viewRecordFieldChangeSchema } from '@salescore/client-redux'
import type { EltChangeResult, EltChangeResultsChunk } from '@salescore/core'
import { Array, HashSet, pipe, Record, Result } from 'effect'
import { isNotUndefined } from 'effect/Predicate'

import { mapAllProperties, type MapAllPropertiesError } from './mapAllProperties'

type EltChangeSuccessResult = Extract<EltChangeResult, { status: 'success' }>
type EltChangeFailureResult = Exclude<EltChangeResult, { status: 'success' }>

export const partitionChangeResults = (
  resultsChunks: EltChangeResultsChunk[],
): {
  successes: EltChangeSuccessResult[]
  errors: EltChangeFailureResult[]
  temporaryToRealRecordIdMap: Record<string, string>
} => {
  const [successes, errors] = pipe(
    Array.flatMap(resultsChunks, (x) => x.results),
    Array.partition((x) => (x.status === 'success' ? Result.succeed(x) : Result.fail(x))),
  )
  const temporaryToRealRecordIdMap: Record<string, string> = pipe(
    successes.filter((x) => x.change.type === 'create'),
    Record.fromIterableWith((x) => [x.change.id, x.result.id]),
  )
  return { successes, errors, temporaryToRealRecordIdMap }
}

/**
 * 保存に失敗したレコードへの変更だけを残し、その中の一時 ID を保存で確定した実 ID に置き換える
 */
export const restoreFailedChanges = ({
  changesToSave,
  errors,
  temporaryToRealRecordIdMap,
}: {
  changesToSave: ChangeHistory[]
  errors: EltChangeFailureResult[]
  temporaryToRealRecordIdMap: Record<string, string>
}): Array<{ change: ChangeHistory; mapErrors: MapAllPropertiesError[] }> => {
  const errorIds = HashSet.fromIterable(errors.map((x) => x.change.id))
  return pipe(
    changesToSave,
    Array.map((change) => {
      const [newChanges, mapErrors] = pipe(
        change.changes,
        Array.filter((fieldChange) => fieldChange.id !== undefined && HashSet.has(errorIds, fieldChange.id)),
        Array.map((fieldChange) =>
          mapAllProperties(fieldChange, temporaryToRealRecordIdMap, viewRecordFieldChangeSchema),
        ),
        Array.separate,
      )
      return newChanges.length === 0
        ? undefined
        : {
            change: {
              ...change,
              changes: newChanges,
              // eslint-disable-next-line sonarjs/todo-tag -- snapshot の扱いが未決のため TODO を残す
              // TODO: snapshotとかどうしようね…。
            },
            mapErrors,
          }
    }),
    Array.filter(isNotUndefined),
  )
}
